// ============================================================
// Drawing an arena by hand (v8.204.0).
//
// While a draft is open every build click feeds the ring instead of the
// terrain - and the points are the RAW unprojection, never the build's
// clamped cell, whose clamp would forbid drawing outside the current
// arena, which is the whole point of the tool.
// ============================================================
import { useState, useCallback } from 'react';
import { project, unprojectToPlane, SCENE_VIEW } from '../engine/battlefield/projection';
import { arenaBoundary } from '../engine/battlefield/arenaShapes';
import { isDrawableRing } from '../engine/battlefield/terrainEdits';
import { pointerToScene } from '../utils/scenePointer';

/** Screen pixels within which a click lands on the ring's first corner. */
const CLOSE_RADIUS_PX = 12;
/** Metres within which a click repeats the last corner and adds nothing. */
const REPEAT_RADIUS_M = 0.1;

/** The house grammar: the right button takes the last corner away. */
const dropLastCorner = (draft) => (draft && draft.length ? draft.slice(0, -1) : draft);

/** What a left click at screen point `p` (ground point `ground`) does to the draft. */
function arenaClick(draft, p, ground, camera) {
  if (draft.length >= 3) {
    const first = project({ x: draft[0].x, y: 0, z: draft[0].z }, camera, SCENE_VIEW);
    if (Math.hypot(first.x - p.x, first.y - p.y) < CLOSE_RADIUS_PX) return 'close';
  }
  const prev = draft[draft.length - 1];
  if (prev && Math.hypot(prev.x - ground.x, prev.z - ground.z) < REPEAT_RADIUS_M) return 'repeat';
  return 'add';
}

/**
 * The arena draft and the boundary it commits to.
 *
 * `arenaPointerDown` is the build's pointer-down while a draft is open;
 * it returns true when it took the event.
 */
export function useArenaDraw({ getCamera, terrain, setTerrain }) {
  const [arenaDraft, setArenaDraft] = useState(null);
  const setBoundary = useCallback((next) => setTerrain({ ...terrain, boundary: next }), [terrain, setTerrain]);
  const startArenaDraw = useCallback(() => setArenaDraft([]), []);
  const cancelArenaDraw = useCallback(() => setArenaDraft(null), []);
  /** Commit the ring - refused, draft kept for repair, while it is degenerate. */
  const finishArenaDraw = useCallback(() => {
    if (!isDrawableRing(arenaDraft)) return false;
    setBoundary(arenaBoundary(terrain.boundary, { vertices: arenaDraft, shape: 'custom' }));
    setArenaDraft(null);
    return true;
  }, [arenaDraft, terrain.boundary, setBoundary]);
  const arenaPointerDown = useArenaClick({ getCamera, arenaDraft, setArenaDraft, finishArenaDraw });
  return { arenaDraft, startArenaDraw, cancelArenaDraw, finishArenaDraw, setBoundary, arenaPointerDown };
}

/** A click while drawing: add a corner, take one back, or close the ring. */
function useArenaClick({ getCamera, arenaDraft, setArenaDraft, finishArenaDraw }) {
  return useCallback(
    (e) => {
      if (e.button === 2) {
        setArenaDraft(dropLastCorner);
        return true;
      }
      if (e.button !== 0) return false;
      const p = pointerToScene(e);
      const camera = getCamera();
      const ground = unprojectToPlane(p.x, p.y, 0, camera, SCENE_VIEW);
      if (!ground) return true;
      const verdict = arenaClick(arenaDraft, p, ground, camera);
      if (verdict === 'close') finishArenaDraw();
      if (verdict === 'add') setArenaDraft([...arenaDraft, { x: ground.x, z: ground.z }]);
      return true;
    },
    [getCamera, arenaDraft, setArenaDraft, finishArenaDraw],
  );
}
